/**
 * <kunnie-withdrawal> — "Vertrag widerrufen" (sections/kunnie-withdrawal.liquid), § 356a BGB.
 *
 * One step, like the § 312k page (assets/kunnie-cancel.js): the form asks for name, e-mail and order number
 * (optionally which items), "Widerruf bestätigen" submits it to data-endpoint (app proxy → kunnie-api
 * POST /widerruf). The server stores the declaration with its time of receipt and answers
 * { ok, receivedAtText, reference, status, statusText, declaration[], mailed, mailLimited }. The page then shows
 * exactly that (printable via window.print()). The e-mail is only mentioned as sent when the server says
 * mailed: true. Errors keep the form filled and say that nothing was submitted yet.
 * ?order=<name> prefills the order number. Logged-in customers see the items of the typed order as checkboxes
 * (rendered by Liquid in [data-order-items]); otherwise the items are described in the text field.
 */

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

const EMAIL = /^[^\s@"'<>(),;:\\]+@[^\s@"'<>(),;:\\]+\.[^\s@"'<>(),;:\\]{2,}$/;
const digits = (value) => String(value ?? '').replace(/\D/g, '');

class KunnieWithdrawal extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {Record<string, string>} */
  #i18n = {};
  #busy = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    try {
      // Liquid's `t` already HTML-escapes; decode once so the texts can be escaped exactly once here.
      const decoder = document.createElement('textarea');
      const raw = JSON.parse(this.querySelector('[data-i18n]')?.textContent || '{}');
      this.#i18n = Object.fromEntries(
        Object.entries(raw).map(([key, value]) => {
          decoder.innerHTML = String(value);
          return [key, decoder.value];
        })
      );
    } catch {
      this.#i18n = {};
    }
    const form = this.#form;
    const order = new URLSearchParams(window.location.search).get('order');
    const orderInput = /** @type {HTMLInputElement | null} */ (form?.elements.namedItem('orderReference') ?? null);
    if (order && orderInput && !orderInput.value) orderInput.value = order.slice(0, 100);

    form?.addEventListener('submit', this.#onSubmit, { signal });
    form?.addEventListener('change', () => this.#syncVisibility(), { signal });
    form?.addEventListener(
      'input',
      (event) => {
        const target = /** @type {HTMLInputElement} */ (event.target);
        if (target.name === 'orderReference') this.#syncVisibility();
        this.#clearError(target.name === 'itemsText' || target.hasAttribute('data-item') ? 'items' : target.name);
      },
      { signal }
    );
    this.querySelector('[data-print]')?.addEventListener('click', () => window.print(), { signal });
    this.querySelector('[data-copy-address]')?.addEventListener('click', this.#copyAddress, { signal });
    this.#syncVisibility();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('[data-form]'));
  }

  #value(name) {
    const form = this.#form;
    if (!form) return '';
    const field = form.elements.namedItem(name);
    if (field instanceof RadioNodeList) return field.value;
    return /** @type {HTMLInputElement | null} */ (field)?.value?.trim() ?? '';
  }

  /** The item list of the typed order (logged-in customers only), if any. */
  #orderItems() {
    const ref = digits(this.#value('orderReference'));
    if (!ref) return null;
    return /** @type {HTMLElement | null} */ (
      [...this.querySelectorAll('[data-order-items]')].find((element) => digits(/** @type {HTMLElement} */ (element).dataset.orderItems) === ref) ?? null
    );
  }

  #syncVisibility() {
    const partial = this.#value('scope') === 'partial';
    for (const element of this.querySelectorAll('[data-show-for-scope]')) {
      element.toggleAttribute('hidden', /** @type {HTMLElement} */ (element).dataset.showForScope !== (partial ? 'partial' : 'all'));
    }
    const current = this.#orderItems();
    for (const element of this.querySelectorAll('[data-order-items]')) element.toggleAttribute('hidden', element !== current);
  }

  /** Checked items of the visible order list. */
  #selectedItems() {
    const list = this.#orderItems();
    if (!list) return [];
    return [...list.querySelectorAll('[data-item]:checked')].map((box) => {
      const input = /** @type {HTMLInputElement} */ (box);
      const qty = /** @type {HTMLInputElement | null} */ (input.closest('.kunnie-withdrawal__item')?.querySelector('[data-item-qty]') ?? null);
      const max = Number(input.dataset.max) || 1;
      const quantity = Math.min(max, Math.max(1, Math.floor(Number(qty?.value) || max)));
      return { id: input.value, title: input.dataset.title || '', quantity };
    });
  }

  /** @param {string} name @param {string} message */
  #setError(name, message) {
    const slot = this.querySelector(`[data-error-for="${name}"]`);
    const wrap = this.querySelector(`[data-field-wrap="${name}"]`);
    const input = /** @type {HTMLElement | null} */ (this.#form?.querySelector(`[name="${name === 'items' ? 'itemsText' : name}"]`) ?? null);
    if (slot) {
      slot.textContent = message;
      slot.toggleAttribute('hidden', !message);
    }
    wrap?.classList.toggle('is-invalid', Boolean(message));
    input?.setAttribute('aria-invalid', message ? 'true' : 'false');
  }

  /** @param {string} name */
  #clearError(name) {
    if (name) this.#setError(name, '');
  }

  #validate() {
    const errors = {};
    if (!this.#value('name')) errors.name = this.#i18n.error_name;
    if (!EMAIL.test(this.#value('email'))) errors.email = this.#i18n.error_email;
    if (!this.#value('orderReference')) errors.orderReference = this.#i18n.error_orderReference;
    if (this.#value('scope') === 'partial' && !this.#selectedItems().length && !this.#value('itemsText')) errors.items = this.#i18n.error_items;
    return errors;
  }

  /** @param {Record<string, string>} errors */
  #showErrors(errors) {
    for (const name of ['name', 'email', 'orderReference', 'items']) this.#setError(name, errors[name] || '');
    const first = ['name', 'email', 'orderReference', 'items'].find((name) => errors[name]);
    if (first) /** @type {HTMLElement | null} */ (this.#form?.querySelector(`[name="${first === 'items' ? 'itemsText' : first}"]`))?.focus();
  }

  /** @param {string} message */
  #formError(message) {
    const box = this.querySelector('[data-form-error]');
    if (!box) return;
    box.textContent = message;
    box.toggleAttribute('hidden', !message);
    if (message) box.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy) return;
    this.#formError('');
    const errors = this.#validate();
    if (Object.keys(errors).length) return this.#showErrors(errors);

    const partial = this.#value('scope') === 'partial';
    const body = {
      locale: document.documentElement.lang || 'de',
      name: this.#value('name'),
      email: this.#value('email'),
      orderReference: this.#value('orderReference'),
      scope: partial ? 'partial' : 'all',
      items: partial ? this.#selectedItems() : [],
      itemsText: partial ? this.#value('itemsText') || null : null,
    };

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-submit]'));
    this.#busy = true;
    button?.setAttribute('aria-busy', 'true');
    if (button) {
      button.disabled = true;
      button.textContent = this.#i18n.sending || button.textContent;
    }
    try {
      const response = await fetch(this.dataset.endpoint || '/apps/kunnie/widerruf', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      // Shopify replaces proxy failures with an HTML page, so only trust JSON.
      if (!response.ok || !(response.headers.get('content-type') || '').includes('application/json')) throw new Error('http');
      const result = await response.json();
      if (!result.ok) {
        if (result.error === 'invalid' && Array.isArray(result.fields)) {
          return this.#showErrors(Object.fromEntries(result.fields.map((name) => [name, this.#i18n[`error_${name}`] || this.#i18n.error_generic])));
        }
        return this.#formError(result.error === 'rate_limited' ? this.#i18n.error_rate || '' : this.#i18n.error_generic || '');
      }
      this.#showResult(result, body.email);
    } catch {
      this.#formError(this.#i18n.error_generic || '');
    } finally {
      this.#busy = false;
      button?.removeAttribute('aria-busy');
      if (button) {
        button.disabled = false;
        button.textContent = this.#i18n.submit || button.textContent;
      }
    }
  };

  /** @param {any} result @param {string} email */
  #showResult(result, email) {
    const text = (key, values = {}) => {
      let value = this.#i18n[key] || '';
      for (const [name, replacement] of Object.entries(values)) value = value.replaceAll(`%${name}%`, String(replacement));
      return value;
    };
    const set = (selector, value) => {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    };
    set('[data-received]', text('done_received', { date: result.receivedAtText || '' }));
    set('[data-reference]', text('done_reference', { reference: result.reference || '' }));
    set('[data-status]', result.statusText || '');
    const list = this.querySelector('[data-declaration]');
    if (list) list.innerHTML = (result.declaration || []).map((line) => `<li>${escapeHtml(line)}</li>`).join('');
    const mail = this.querySelector('[data-mail]');
    if (mail) {
      mail.textContent = result.mailed ? text('done_mailed', { email }) : result.mailLimited ? text('done_limited') : text('done_not_mailed');
      mail.classList.toggle('is-warning', !result.mailed);
    }
    this.#form?.setAttribute('hidden', '');
    this.querySelector('.kunnie-cancel__intro')?.setAttribute('hidden', '');
    const resultBox = this.querySelector('[data-result]');
    resultBox?.removeAttribute('hidden');
    /** @type {HTMLElement | null} */ (this.querySelector('[data-focus-result]'))?.focus();
    resultBox?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  /** @param {MouseEvent} event */
  #copyAddress = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const address = /** @type {HTMLElement | null} */ (this.querySelector('[data-return-address]'))?.innerText.trim() || '';
    try {
      await navigator.clipboard.writeText(address);
      button.textContent = this.#i18n.copied || button.textContent;
      setTimeout(() => (button.textContent = this.#i18n.copy || button.textContent), 2500);
    } catch {
      // Clipboard blocked: select the address so it can be copied by hand.
      const range = document.createRange();
      const node = this.querySelector('[data-return-address]');
      if (!node) return;
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };
}

if (!customElements.get('kunnie-withdrawal')) customElements.define('kunnie-withdrawal', KunnieWithdrawal);
